import React, { useEffect, useState } from "react";
import unitReportService, {
  KPIData,
  ProjectStatusData,
  SalesTimelineData,
  StatusData,
  UnitDetail,
  UnitTypeData,
  WingData,
} from "../services/unitReportService";
import UnitReportGrid from "./UnitReportGrid";
import "../css/UnitReport.css";

interface Project {
  id: number;
  name: string;
}

const UnitReport: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState<{
    title: string;
    units: UnitDetail[];
  }>({ title: "", units: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State for actual data
  const [projects, setProjects] = useState<Project[]>([]);
  const [kpiData, setKpiData] = useState<KPIData>({
    totalUnits: 0,
    availableUnits: 0,
    bookedUnits: 0,
    soldUnits: 0,
    blockedUnits: 0,
  });
  const [statusData, setStatusData] = useState<StatusData>({
    sold: 0,
    unsold: 0,
    booked: 0,
    hold: 0,
    approval_pending: 0,
  });
  const [unitTypeData, setUnitTypeData] = useState<UnitTypeData[]>([]);
  const [wingData, setWingData] = useState<WingData[]>([]);
  const [projectStatusData, setProjectStatusData] = useState<ProjectStatusData[]>([]);
  const [salesTimelineData, setSalesTimelineData] = useState<SalesTimelineData[]>([]);
  const [reportView, setReportView] = useState<'charts' | 'grid'>('charts');

  // Load data
  const loadData = async (isInitialLoad = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await unitReportService.getUnitReportData(
        selectedProject === "all" ? undefined : selectedProject,
        dateFrom || undefined,
        dateTo || undefined
      );

      setKpiData(data.kpi);
      setStatusData(data.statusData);
      setUnitTypeData(data.unitTypeData);
      setWingData(data.wingData);
      setProjectStatusData(data.projectStatusData);
      setSalesTimelineData(data.salesTimeline);
      
      // Only update projects list on initial load or when "all" is selected
      if (isInitialLoad || selectedProject === "all" || selectedProject === "") {
        setProjects(data.projects);
      }

      // Set first project as default if not already set (only on initial load)
      if (isInitialLoad && !selectedProject && data.projects.length > 0) {
        setSelectedProject(data.projects[0].id.toString());
      }
    } catch (err: any) {
      console.error("Error loading unit report:", err);
      setError(err.response?.data?.message || "Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount and when first project is set
  useEffect(() => {
    loadData(true); // Pass true for initial load
  }, []);

  // Load data when filters change (after initial load)
  useEffect(() => {
    if (selectedProject) {
      loadData(false); // Pass false for subsequent loads
    }
  }, [selectedProject, dateFrom, dateTo]);

  const handleApplyFilters = () => {
    loadData(false);
  };

  const handleResetFilters = () => {
    setSelectedProject(projects.length > 0 ? projects[0].id.toString() : "all");
    setDateFrom("");
    setDateTo("");
  };

  const handleChartClick = async (title: string, filters: any) => {
    try {
      setLoading(true);
      const units = await unitReportService.getUnitDetails(
        selectedProject === "all" ? undefined : selectedProject,
        filters.status,
        filters.type,
        filters.wing
      );
      setModalData({ title, units });
      setShowModal(true);
    } catch (err: any) {
      console.error("Error loading unit details:", err);
      alert("Failed to load unit details");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      sold: "#28a745",
      unsold: "#ff6b6b",
      booked: "#90ee90",
      hold: "#dc3545",
      approval_pending: "#6c757d",
    };
    return colors[status] || "#6c757d";
  };

  return (
    <div className="unit-report-container">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">Loading...</div>
        </div>
      )}

      <div className="report-header">
        <div className="report-header-top">
          <div>
            <h1>Unit Availability Report</h1>
            <p className="report-description">
              Complete overview of unit status, sales, and inventory analysis
            </p>
          </div>
          <div className="report-view-toggle">
            <button
              className={`view-toggle-btn ${reportView === 'charts' ? 'active' : ''}`}
              onClick={() => setReportView('charts')}
            >
              Charts View
            </button>
            <button
              className={`view-toggle-btn ${reportView === 'grid' ? 'active' : ''}`}
              onClick={() => setReportView('grid')}
            >
              Grid View
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
        </div>
      )}

      {reportView === 'grid' ? (
        <>
        <div className="report-filters">
          <div className="filter-group">
            <label>Project</label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="filter-select"
            >
              <option value="">-- Select Project --</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <UnitReportGrid
          projectId={selectedProject}
          userProjects={projects}
        />
        </>
      ) : (
      <>
      {/* Filters */}
      <div className="report-filters" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyFilters(); } }}>
        <div className="filter-group">
          <label>Project</label>
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="filter-select"
            disabled={loading}
          >
            <option value="all">All Projects</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Date From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="filter-input"
            disabled={loading}
          />
        </div>

        <div className="filter-group">
          <label>Date To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="filter-input"
            disabled={loading}
          />
        </div>

        <button 
          className="btn-primary" 
          onClick={handleApplyFilters}
          disabled={loading}
        >
          Apply
        </button>
        <button
          className="btn-secondary"
          onClick={handleResetFilters}
          disabled={loading}
        >
          Reset
        </button>
      </div>

      {/* Charts Grid */}
      <div className="charts-grid">
        {/* Chart 1: Overall Status Distribution */}
        <div className="chart-card">
          <h3 className="chart-title">Overall Status Distribution</h3>
          <div className="chart-content">
            <div className="donut-chart">
              {Object.entries(statusData).map(([status, count]) => {
                const total = Object.values(statusData).reduce((a, b) => a + b, 0);
                const percentage = ((count / total) * 100).toFixed(1);
                return (
                  <div
                    key={status}
                    className="donut-segment"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleChartClick(`${status.toUpperCase()} Units`, { status })}
                  >
                    <div
                      className="segment-bar"
                      style={{
                        width: `${percentage}%`,
                        background: getStatusColor(status),
                      }}
                    />
                    <div className="segment-label">
                      <span className="status-name">{status.replace("_", " ")}</span>
                      <span className="status-count">
                        {count} ({percentage}%)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Chart 2: Project-wise Status */}
        <div className="chart-card chart-card-wide">
          <h3 className="chart-title">Project-wise Status Overview</h3>
          <div className="chart-content">
            <div className="bar-chart-horizontal">
              {projectStatusData.map((project) => (
                <div key={project.projectName} className="bar-group">
                  <div className="bar-label">{project.projectName}</div>
                  <div className="bar-container">
                    <div
                      className="bar-segment sold"
                      style={{
                        width: `${
                          (project.sold /
                            (project.sold +
                              project.unsold +
                              project.booked +
                              project.hold +
                              project.approval_pending)) *
                          100
                        }%`,
                      }}
                      onClick={() =>
                        handleChartClick(`${project.projectName} - Sold Units`, project)
                      }
                      title={`Sold: ${project.sold}`}
                    >
                      {project.sold > 0 && project.sold}
                    </div>
                    <div
                      className="bar-segment unsold"
                      style={{
                        width: `${
                          (project.unsold /
                            (project.sold +
                              project.unsold +
                              project.booked +
                              project.hold +
                              project.approval_pending)) *
                          100
                        }%`,
                      }}
                      onClick={() =>
                        handleChartClick(`${project.projectName} - Unsold Units`, project)
                      }
                      title={`Unsold: ${project.unsold}`}
                    >
                      {project.unsold > 0 && project.unsold}
                    </div>
                    {project.booked > 0 && (
                      <div
                        className="bar-segment booked"
                        style={{
                          width: `${
                            (project.booked /
                              (project.sold +
                                project.unsold +
                                project.booked +
                                project.hold +
                                project.approval_pending)) *
                            100
                          }%`,
                        }}
                        onClick={() =>
                          handleChartClick(`${project.projectName} - Booked Units`, project)
                        }
                        title={`Booked: ${project.booked}`}
                      >
                        {project.booked}
                      </div>
                    )}
                    {project.hold > 0 && (
                      <div
                        className="bar-segment hold"
                        style={{
                          width: `${
                            (project.hold /
                              (project.sold +
                                project.unsold +
                                project.booked +
                                project.hold +
                                project.approval_pending)) *
                            100
                          }%`,
                        }}
                        onClick={() =>
                          handleChartClick(`${project.projectName} - Hold Units`, project)
                        }
                        title={`Hold: ${project.hold}`}
                      >
                        {project.hold}
                      </div>
                    )}
                    {project.approval_pending > 0 && (
                      <div
                        className="bar-segment pending"
                        style={{
                          width: `${
                            (project.approval_pending /
                              (project.sold +
                                project.unsold +
                                project.booked +
                                project.hold +
                                project.approval_pending)) *
                            100
                          }%`,
                        }}
                        onClick={() =>
                          handleChartClick(`${project.projectName} - Pending Units`, project)
                        }
                        title={`Pending: ${project.approval_pending}`}
                      >
                        {project.approval_pending}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="chart-legend">
              <span className="legend-item">
                <span className="legend-color sold"></span> Sold
              </span>
              <span className="legend-item">
                <span className="legend-color unsold"></span> Unsold
              </span>
              <span className="legend-item">
                <span className="legend-color booked"></span> Booked
              </span>
              <span className="legend-item">
                <span className="legend-color hold"></span> Hold
              </span>
              <span className="legend-item">
                <span className="legend-color pending"></span> Pending
              </span>
            </div>
          </div>
        </div>

        {/* Chart 3: Unit Type Distribution */}
        <div className="chart-card">
          <h3 className="chart-title">Unit Type Distribution</h3>
          <div className="chart-content">
            <div className="bar-chart-vertical">
              {unitTypeData.map((type) => {
                const maxValue = Math.max(...unitTypeData.map((t) => t.total));
                return (
                  <div key={type.type} className="bar-column">
                    <div className="bar-stack">
                      <div
                        className="bar-part sold-bar"
                        style={{
                          height: `${(type.sold / maxValue) * 100}%`,
                        }}
                        onClick={() => handleChartClick(`${type.type} - Sold`, type)}
                        title={`Sold: ${type.sold}`}
                      >
                        <span>{type.sold}</span>
                      </div>
                      <div
                        className="bar-part unsold-bar"
                        style={{
                          height: `${(type.unsold / maxValue) * 100}%`,
                        }}
                        onClick={() => handleChartClick(`${type.type} - Unsold`, type)}
                        title={`Unsold: ${type.unsold}`}
                      >
                        <span>{type.unsold}</span>
                      </div>
                      <div
                        className="bar-part booked-bar"
                        style={{
                          height: `${(type.booked / maxValue) * 100}%`,
                        }}
                        onClick={() => handleChartClick(`${type.type} - Booked`, type)}
                        title={`Booked: ${type.booked}`}
                      >
                        <span>{type.booked}</span>
                      </div>
                    </div>
                    <div className="bar-label-bottom">{type.type}</div>
                  </div>
                );
              })}
            </div>
            <div className="chart-legend">
              <span className="legend-item">
                <span className="legend-color sold"></span> Sold
              </span>
              <span className="legend-item">
                <span className="legend-color unsold"></span> Unsold
              </span>
              <span className="legend-item">
                <span className="legend-color booked"></span> Booked
              </span>
            </div>
          </div>
        </div>

        {/* Chart 4: Wing-wise Analysis */}
        <div className="chart-card">
          <h3 className="chart-title">Wing-wise Analysis</h3>
          <div className="chart-content">
            <div className="bar-chart-vertical">
              {wingData.map((wing) => {
                const maxValue = Math.max(...wingData.map((w) => w.total));
                return (
                  <div key={wing.wing} className="bar-column">
                    <div className="bar-stack">
                      <div
                        className="bar-part sold-bar"
                        style={{
                          height: `${(wing.sold / maxValue) * 100}%`,
                        }}
                        onClick={() => handleChartClick(`Wing ${wing.wing} - Sold`, wing)}
                        title={`Sold: ${wing.sold}`}
                      >
                        <span>{wing.sold}</span>
                      </div>
                      <div
                        className="bar-part unsold-bar"
                        style={{
                          height: `${(wing.unsold / maxValue) * 100}%`,
                        }}
                        onClick={() => handleChartClick(`Wing ${wing.wing} - Unsold`, wing)}
                        title={`Unsold: ${wing.unsold}`}
                      >
                        <span>{wing.unsold}</span>
                      </div>
                      <div
                        className="bar-part booked-bar"
                        style={{
                          height: `${(wing.booked / maxValue) * 100}%`,
                        }}
                        onClick={() => handleChartClick(`Wing ${wing.wing} - Booked`, wing)}
                        title={`Booked: ${wing.booked}`}
                      >
                        <span>{wing.booked}</span>
                      </div>
                    </div>
                    <div className="bar-label-bottom">Wing {wing.wing}</div>
                  </div>
                );
              })}
            </div>
            <div className="chart-legend">
              <span className="legend-item">
                <span className="legend-color sold"></span> Sold
              </span>
              <span className="legend-item">
                <span className="legend-color unsold"></span> Unsold
              </span>
              <span className="legend-item">
                <span className="legend-color booked"></span> Booked
              </span>
            </div>
          </div>
        </div>

        {/* Chart 5: Sales Timeline */}
        <div className="chart-card chart-card-wide">
          <h3 className="chart-title">Sales Timeline (Last 6 Months)</h3>
          <div className="chart-content">
            <div className="line-chart">
              {salesTimelineData.map((data, index) => {
                const maxValue = Math.max(...salesTimelineData.map((d) => d.units));
                return (
                  <div key={data.month} className="line-bar">
                    <div
                      className="line-bar-fill"
                      style={{
                        height: `${(data.units / maxValue) * 100}%`,
                      }}
                      onClick={() =>
                        handleChartClick(`${data.month} Sales`, data)
                      }
                    >
                      <span className="line-value">{data.units}</span>
                    </div>
                    <div className="line-label">{data.month}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Modal for drill-down */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalData.title}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Sr. No</th>
                      <th>Wing</th>
                      <th>Unit No</th>
                      <th>Type</th>
                      <th>Size</th>
                      <th>Status</th>
                      <th>Sale Amount</th>
                      <th>Booking Date</th>
                      <th>Customer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalData.units.map((unit, index) => (
                      <tr key={unit.id}>
                        <td>{index + 1}</td>
                        <td>{unit.wing || '-'}</td>
                        <td>{unit.unitNo}</td>
                        <td>{unit.unitType}</td>
                        <td>{unit.size}</td>
                        <td>
                          <span className={`status-badge status-${unit.status}`}>
                            {unit.status}
                          </span>
                        </td>
                        <td>{formatCurrency(unit.saleDeedAmount)}</td>
                        <td>{unit.bookingDate || "-"}</td>
                        <td>{unit.customerName || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>
                Close
              </button>
              <button className="btn-primary">Export to Excel</button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default UnitReport;
